// ============================================================================
// LA BARRE DE FILTRES RESPONSIVE — recherche toujours visible, filtres en TIROIR sur mobile.
//
// ⚠️ POURQUOI AU SOCLE (décision utilisateur 2026-08-24, passe responsive) : les barres du journal des
// ventes et du catalogue alignent recherche + pickers + période sur UNE ligne — sur téléphone elles
// s'empilent en colonne et mangent la moitié de l'écran avant la première ligne du tableau. La règle
// retenue : « juste la recherche, et pour les filtres un off-canvas ». Un composant par écran aurait
// autant de variantes que d'écrans.
//
// ⚠️ LES FILTRES RESTENT LES MÊMES COMPOSANTS aux deux tailles : pickers et périodes sont rendus tels
// quels dans le tiroir (leurs panneaux s'ouvrent AU-DESSUS — popover z1000 > drawer z700, l'échelle
// partagée le garantit). Aucun bouton « Appliquer » : un filtre agit au geste, le tableau suit —
// c'est la doctrine des tiroirs de réglage.
// ============================================================================
import { useState, type ReactNode } from 'react';
import { RAIL_BREAKPOINT } from '@orgazone/ui-tokens';
import { SideDrawer } from './drawer';
import { Icon } from './icons';
import { useMediaMax } from './shell';
import { t } from '@orgazone/i18n';

// ⚠️ SUR MOBILE, LE COMPOSANT REND UN FRAGMENT (recherche pleine ligne + bouton « Filtres ») et compte
// sur la RANGÉE FLEX-WRAP de son hôte pour la mise en ligne — la barre du DataTable (`.dt-toolbar`)
// l'est. Hors d'une rangée flex, l'hôte doit en fournir une.
export interface FilterToolbarProps {
  /** La recherche — seule survivante de la barre sur mobile, pleine largeur. */
  search: ReactNode;
  /** Les filtres (pickers, période…) — en ligne sur desktop, empilés dans le tiroir sur mobile. */
  filters: ReactNode;
  /** Combien de filtres sont ACTIFS — le badge du bouton « Filtres » le dit sans ouvrir. */
  actifs?: number;
  /** Titre du tiroir mobile. */
  title?: string;
}

/** Recherche + filtres : une ligne sur desktop, recherche + bouton « Filtres » (tiroir) sur mobile. */
// ⚠️ UNE SEULE GOUTTIÈRE POUR TOUTES LES BARRES (décision utilisateur 2026-08-25) : les maquettes
// divergeaient d'UN pixel (9 au catalogue, 10 au journal) — un écart que personne ne voit et qui
// coûterait un réglage par écran à maintenir.
export function FilterToolbar({ search, filters, actifs = 0, title = t('Filtres') }: FilterToolbarProps) {
  const gap = 10;
  const mobile = useMediaMax(RAIL_BREAKPOINT);
  const [ouvert, setOuvert] = useState(false);

  if (!mobile) {
    // ⚠️ `flex: 1` ET `minWidth: 0` — CE N'EST PAS DE LA DÉCORATION (régression du 2026-08-24, corrigée
    // le 25). Sans eux, la barre prend sa largeur NATURELLE dans la rangée du tableau : la recherche
    // (qui s'étire par `flex: 1` DANS cette barre) se tasse, et les derniers filtres tombent sur une
    // seconde ligne — alors que le groupe Colonnes/Exporter, poussé à droite, reste bien placé. C'est
    // exactement ce que portaient les barres d'origine avant l'extraction au socle.
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap, flexWrap: 'wrap', flex: 1, minWidth: 0 }}>
        {search}
        {filters}
      </div>
    );
  }
  return (
    <>
      {/* ⚠️ DEUX LIGNES, PAS UNE (retour utilisateur 2026-08-24) : la recherche occupe SA ligne
          (`flex-basis:100%` — la barre parente est en `flex-wrap`), et le bouton « Filtres » passe à
          la SUIVANTE, où il partage la rangée avec « Colonnes »/« Exporter » du tableau (leur groupe
          porte un `margin-left:auto` : Filtres à gauche, les outils de vue à droite). Sur une seule
          ligne, tout se tassait et rien ne s'alignait. */}
      <div style={{ flexBasis: '100%', minWidth: 0 }}>{search}</div>
      <button
        type="button"
        onClick={() => setOuvert(true)}
        aria-label={actifs > 0 ? t('Filtres — {n} actif', { n: actifs }) : t('Filtres')}
        style={{
          height: 40, padding: '0 13px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)',
          background: 'var(--paper)', color: 'var(--ink-700)', fontSize: 'var(--t-lead)', fontWeight: 600,
          display: 'inline-flex', alignItems: 'center', gap: 7, cursor: 'pointer', flexShrink: 0,
        }}
      >
        <Icon name="sliders" size={14} />
        
        {t('Filtres')}
        {/* Le COMPTE des filtres actifs — sans lui, un tableau filtré et un tableau complet se
            ressemblent, et on cherche des lignes que le filtre cache. */}
        {actifs > 0 && (
          <span style={{
            minWidth: 18, height: 18, padding: '0 5px', borderRadius: 'var(--r-pill)',
            background: 'var(--teal-500)', color: '#fff', fontSize: 'var(--t-meta)', fontWeight: 700,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          }}>{actifs}</span>
        )}
      </button>
      <SideDrawer
        open={ouvert}
        onClose={() => setOuvert(false)}
        title={title}
        subtitle={t('Ils s’appliquent au geste — le tableau suit derrière.')}
      >
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 12 }}>
          {filters}
        </div>
      </SideDrawer>
    </>
  );
}
